import { useEffect, useState, useCallback } from "react";
import api, { fmtIDR } from "@/lib/api";
import { numLang } from "@/lib/num";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download } from "lucide-react";

const M = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

export function DepreciationTab({ clientId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() + 1 - i);
  const load = useCallback(async () => {
    if (!clientId) return;
    const { data: d } = await api.get(`/fixed-assets/depreciation?client_id=${clientId}&year=${year}`);
    setData(d);
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);
  const exportX = async () => {
    try {
      const res = await api.get(`/fixed-assets/depreciation?client_id=${clientId}&year=${year}&fmt=xlsx&lang=${numLang()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `penyusutan_aset_tetap_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal ekspor"); }
  };
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="dep-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={exportX} data-testid="dep-export"><Download className="h-4 w-4 mr-2" />Excel</Button>
      </div>
      {data && (
        <div className="overflow-auto rounded-md border border-border">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead>Aset</TableHead><TableHead className="text-right">Perolehan</TableHead><TableHead className="text-right">Akum. Awal</TableHead>
              {M.map((m) => <TableHead key={m} className="text-right">{m}</TableHead>)}
              <TableHead className="text-right">Total {year}</TableHead><TableHead className="text-right">Nilai Buku Akhir</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.length === 0 && <TableRow><TableCell colSpan={17} className="text-center py-8 text-muted-foreground text-sm">Belum ada aset</TableCell></TableRow>}
              {data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`dep-row-${r.id}`}>
                  <TableCell className="text-xs whitespace-nowrap"><span className="font-medium">{r.name}</span><span className="block text-[10px] text-muted-foreground">{r.code} · {r.category_label}</span></TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(r.cost)}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(r.acc_start)}</TableCell>
                  {r.months.map((v, i) => <TableCell key={i} className="num text-right text-[11px]">{v ? fmtIDR(v) : "—"}</TableCell>)}
                  <TableCell className="num text-right text-xs font-semibold">{fmtIDR(r.total_year)}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(r.nbv_end)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-secondary/50 font-semibold">
                <TableCell>Total</TableCell><TableCell className="num text-right text-xs">{fmtIDR(data.total_cost)}</TableCell><TableCell></TableCell>
                {data.month_totals.map((v, i) => <TableCell key={i} className="num text-right text-[11px]">{fmtIDR(v)}</TableCell>)}
                <TableCell className="num text-right text-xs" data-testid="dep-total-year">{fmtIDR(data.total_year)}</TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(data.total_nbv_end)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
